import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from './icons';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { WS_RADIUS, WS_STATUS, type WsTone } from './tokens';

export type BannerTone = Exclude<WsTone, 'neutral'>;

export interface BannerProps {
  tone: BannerTone;
  title?: string;
  children: ReactNode;
  /** A button for the one next step. */
  action?: ReactNode;
  onDismiss?: () => void;
}

const GLYPH: Record<BannerTone, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

/**
 * A message that stays on the page until it is dealt with: an inline message in a status pair.
 * Errors and warnings are announced at once (`alert`); info and success politely (`status`).
 * The glyph and the words carry the meaning as well as the colour.
 */
export function Banner({ tone, title, children, action, onDismiss }: BannerProps) {
  const pair = WS_STATUS[tone];
  return (
    <Box
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 3,
        px: 4,
        py: 3,
        borderRadius: `${WS_RADIUS.field}px`,
        backgroundColor: pair.fill,
        color: pair.ink,
      }}
    >
      <Box sx={{ display: 'flex', pt: '2px' }}>
        <Icon icon={GLYPH[tone]} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            {title}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ color: 'inherit' }}>
          {children}
        </Typography>
      </Box>
      {action ? <Box sx={{ alignSelf: 'center' }}>{action}</Box> : null}
      {onDismiss ? (
        <IconButton
          icon={X}
          label="Dismiss message"
          size="small"
          onClick={onDismiss}
          sx={{ color: 'inherit', mt: '-6px', mr: '-8px' }}
        />
      ) : null}
    </Box>
  );
}
